import { useLayoutEffect } from 'react';

/**
 * The view transitions this app plays. index.css styles each one under its
 * class on <html>, which is set only while that transition runs.
 * - `layout`: the machine list switches between list and grid.
 * - `push`: a detail page slides in from the right over the page it leaves.
 * - `pop`: the way back. The detail page slides out to the right again.
 */
export type ViewTransitionKind = 'layout' | 'push' | 'pop';

/**
 * Runs `update` inside a view transition of the given kind. Without browser
 * support, or with reduced motion, it runs `update` alone.
 */
export function runViewTransition(
  kind: ViewTransitionKind,
  update: () => void | Promise<void>
): void {
  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  if (!document.startViewTransition || reduceMotion) {
    void update();
    return;
  }

  const className = `vt-${kind}`;
  const root = document.documentElement;
  root.classList.add(className);
  const transition = document.startViewTransition(update);
  void transition.finished.finally(() => root.classList.remove(className));
}

/** A route that has not rendered after this long is shown without waiting. */
const PAGE_READY_TIMEOUT_MS = 500;

let resolvePageReady: (() => void) | null = null;

/**
 * Navigates inside a `push` or `pop` transition. The router renders a
 * navigation as a React transition, after the browser would capture the new
 * page, so the capture waits until the new page calls usePageTransitionReady.
 */
export function navigateWithTransition(
  kind: 'push' | 'pop',
  navigate: () => void
): void {
  runViewTransition(
    kind,
    () =>
      new Promise<void>((resolve) => {
        resolvePageReady = resolve;
        window.setTimeout(resolve, PAGE_READY_TIMEOUT_MS);
        navigate();
      })
  );
}

/** Tells a running `push` or `pop` transition that this page has rendered. */
export function usePageTransitionReady(): void {
  useLayoutEffect(() => {
    resolvePageReady?.();
    resolvePageReady = null;
  }, []);
}
